import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import HomeworkService from "../../../services/HomeworkService";
import "./Performance.css";

const getSubmissions = (response, invalidResponseMessage) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.submissions)) return response.submissions;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.content)) return response.content;
  throw new Error(invalidResponseMessage);
};

const getStudentName = (submission) =>
  submission.studentName ||
  submission.student?.name ||
  submission.student?.fullName ||
  submission.student?.username ||
  `Student ${submission.studentId ?? ""}`.trim();

function RatingStars({ rating, t }) {
  const score = Number(rating);
  const hasRating = Number.isFinite(score) && score >= 0 && score <= 5;

  return (
    <div className="performance-rating" aria-label={hasRating ? t("performanceRatingAria", { rating: score }) : t("notRated")}>
      <span className="performance-stars" aria-hidden="true">
        {[1, 2, 3, 4, 5].map((star) => (
          <span className={hasRating && score >= star ? "is-filled" : ""} key={star}>★</span>
        ))}
      </span>
      <span className="performance-rating-value">{hasRating ? `${score}/5` : t("notRated")}</span>
    </div>
  );
}

function ClipboardIcon() {
  return (
    <svg viewBox="0 0 48 48" aria-hidden="true">
      <rect x="11" y="9" width="26" height="32" rx="4" />
      <path d="M19 9V7a3 3 0 0 1 3-3h4a3 3 0 0 1 3 3v2M18 21h12M18 27h12M18 33h8" />
      <path d="m31 35 2 2 5-6" />
    </svg>
  );
}

export default function Performance() {
  const { t } = useTranslation();
  const [homeworkId, setHomeworkId] = useState("");
  const [submissions, setSubmissions] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const loadSubmissions = async (event) => {
    event.preventDefault();
    const id = homeworkId.trim();
    if (!id) {
      setError(t("performanceEnterHomeworkId"));
      return;
    }

    setLoading(true);
    setError("");
    setSubmissions(null);
    try {
      const response = await HomeworkService.getHomeworkSubmissions(id);
      setSubmissions(getSubmissions(response, t("performanceInvalidResponse")));
    } catch (requestError) {
      console.error("Unable to load homework feedback:", requestError);
      setError(
        requestError.response?.data?.message ||
        requestError.message ||
        t("performanceLoadFailed")
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="pri-content performance-page">
      <section className="performance-header">
        <div className="performance-heading">
          <div className="performance-heading-icon" aria-hidden="true">
            <ClipboardIcon />
          </div>
          <div>
            <p className="performance-eyebrow">{t("homeworkFeedback")}</p>
            <h1>{t("performanceTitle")}</h1>
            <p className="performance-description">{t("performanceDescription")}</p>
          </div>
        </div>
      </section>

      <form className="performance-search pri-card" onSubmit={loadSubmissions}>
        <div className="performance-search-heading">
          <div>
            <h2>{t("performanceFindHomework")}</h2>
            <p>{t("performanceSearchDescription")}</p>
          </div>
        </div>
        <div className="performance-search-row">
          <div className="performance-field">
            <label htmlFor="performance-homework-id">{t("homeworkId")}</label>
            <input
              id="performance-homework-id"
              type="text"
              inputMode="numeric"
              value={homeworkId}
              onChange={(event) => setHomeworkId(event.target.value)}
              placeholder={t("performanceHomeworkIdPlaceholder")}
              aria-describedby={error ? "performance-error" : undefined}
            />
          </div>
          <button className="performance-search-button" type="submit" disabled={loading}>
            {loading ? (
              <span className="performance-spinner" aria-hidden="true" />
            ) : (
              <svg viewBox="0 0 20 20" aria-hidden="true">
                <circle cx="8.5" cy="8.5" r="5.5" />
                <path d="m13 13 4 4" />
              </svg>
            )}
            {loading ? t("performanceLoading") : t("viewPerformance")}
          </button>
        </div>
        {error && <p className="performance-error" id="performance-error" role="alert">{error}</p>}
      </form>

      {submissions === null ? (
        <section className="performance-empty performance-empty-start" aria-live="polite">
          <div className="performance-empty-icon" aria-hidden="true">
            <ClipboardIcon />
          </div>
          <h2>{t("performanceReadyTitle")}</h2>
          <p>{t("performanceReadyDescription")}</p>
        </section>
      ) : (
        <section className="performance-results" aria-live="polite">
          <div className="performance-results-heading">
            <div>
              <h2>{t("submissions")}</h2>
              <p>{t("performanceResultsDescription")}</p>
            </div>
            <span className="performance-count">
              <strong>{submissions.length}</strong>
              {submissions.length === 1 ? t("student") : t("students")}
            </span>
          </div>

          {submissions.length === 0 ? (
            <div className="performance-empty">
              <div className="performance-empty-icon" aria-hidden="true">
                <ClipboardIcon />
              </div>
              <h3>{t("performanceNoSubmissionsTitle")}</h3>
              <p>{t("performanceNoSubmissions")}</p>
            </div>
          ) : (
            <div className="performance-submission-list">
              {submissions.map((submission, index) => {
                const submissionId = submission.id ?? submission.submissionId ?? index;
                const status = submission.teacherStatus || submission.status;
                return (
                  <article className="performance-submission" key={submissionId}>
                    <div className="performance-submission-header">
                      <div className="performance-student">
                        <div className="performance-avatar" aria-hidden="true">
                          {getStudentName(submission).charAt(0).toUpperCase()}
                        </div>
                        <div>
                        <h3>{getStudentName(submission)}</h3>
                        {submission.studentId != null && (
                          <p className="performance-student-id">{t("studentId")}: {submission.studentId}</p>
                        )}
                        </div>
                      </div>
                      <RatingStars rating={submission.rating} t={t} />
                    </div>
                    {submission.answerText && (
                      <div className="performance-section">
                        <h4>{t("studentAnswer")}</h4>
                        <p>{submission.answerText}</p>
                      </div>
                    )}
                    <div className="performance-section">
                      <h4>{t("teacherFeedback")}</h4>
                      <p>{submission.feedback || t("performanceNoWrittenFeedback")}</p>
                    </div>
                    {status && (
                      <span className={`performance-status ${status.toLowerCase() === "checked" ? "is-checked" : ""}`}>
                        <span className="performance-status-dot" />
                        {status.replaceAll("_", " ")}
                      </span>
                    )}
                  </article>
                );
              })}
            </div>
          )}
        </section>
      )}
    </main>
  );
}
